import Space from 'antd/lib/space';
import * as React from 'react';
import { antGrey, runwayColors } from './styles/globalStyles';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import {
    AppleAppStorePhasedReleaseState,
    AppleAppStoreState,
    AppPlatform,
    AppStoreType,
    AutomationAutoStagedRollout,
    AutomationType,
    GooglePlayStoreState,
    HuaweiPhasedReleaseState,
    PhasedRelease,
    RolloutAction,
} from '../types';
import {
    getError,
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsHuaweiAppGallery,
    getIsLoading,
    getRolloutDaysToPercentageFromAutomation,
    getUpdated,
    isPhasedReleaseComplete,
    isPhasedReleasePaused,
    isPhasedReleaseSuperseded,
} from '../helpers';
import Popconfirm from 'antd/lib/popconfirm';
import {
    CaretRightOutlined,
    PauseOutlined,
    PercentageOutlined,
    PlayCircleFilled,
    RiseOutlined,
} from '@ant-design/icons';
import { ReleaseUpdatable } from '../actions/updatable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import UpdateStagedRolloutModal from './UpdateStagedRolloutModal';
import PhasedReleasePercent from './phasedReleaseModule/PhasedReleasePercent';
import { PhasedReleaseProgress } from './phasedReleaseModule/PhasedReleaseProgress';
import PhasedReleaseDayText from './phasedReleaseModule/PhasedReleaseDayText';
import { usePhasedReleaseMessages } from './phasedReleaseModule/usePhasedReleaseMessages';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import Menu from 'antd/lib/menu';
import {
    BaseUpdateReleaseRequest,
    tryUpdateRelease,
    UpdateApplePhasedReleaseRequestReleaseStep,
    UpdateGooglePhasedReleaseRequestReleaseStep,
    UpdateHuaweiPhasedReleaseRequestReleaseStep,
} from '@/actions/release';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { selectAutomationById, selectUserCanPerformAction } from '@/selectors/selectors';
import { StepRunUpdatable } from '@/actions/stepRun';
import { getRolloutPercentText } from '../helpers/displayHelpers';
import { Tooltip } from 'antd/lib';

interface PhaseReleaseModuleProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    phasedRelease?: PhasedRelease;
    appleState?: AppleAppStoreState;
    googleState?: GooglePlayStoreState;
    huaweiState?: HuaweiPhasedReleaseState;
    autoHaltUnstablePhasedReleasesAutomationId: Nullable<string>;
    autoPromoteStablePhasedReleasesAutomationId: Nullable<string>;
    autoStagedRolloutAutomationId: Nullable<string>;
}

export interface PhasedReleaseModuleCTAsProps extends PhaseReleaseModuleProps {
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    setStagedRolloutEditModalVisible: (visible: boolean) => void;
    autoSelectAutomations: boolean;
    setAutoSelectAutomations: (autoSelect: boolean) => void;
    autoStagedRolloutAutomationId: Nullable<string>;
}

export const PhasedReleaseModuleCTAs: React.FC<PhasedReleaseModuleCTAsProps> = (props) => {
    const dispatch = useDispatch();

    const rolloutStartedAt = props.phasedRelease?.startDate;

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;

    const isGooglePlayStore = getIsGooglePlayStore(props.appStoreType);
    const isAppleAppStore = getIsAppleAppStore(props.appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(props.appStoreType);

    const rolloutComplete = isPhasedReleaseComplete(props.phasedRelease);
    const phasedReleasePaused = isPhasedReleasePaused(props.phasedRelease?.state);
    const isSuperseded = isPhasedReleaseSuperseded(props.phasedRelease);

    const [pauseGoogleStagedRelease, pauseGoogleStagedReleaseIsLoading] = useCallUpdatable(
        StepRunUpdatable.PauseGooglePhasedRelease,
        props.stepRunComponentProps,
    );

    const haltGoogleStagedReleaseIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.HaltGooglePhasedRelease, state, props.releaseId),
    );
    useSelector((state: RootState) => getError(ReleaseUpdatable.HaltGooglePhasedRelease, state, props.releaseId));
    useSelector((state: RootState) => getUpdated(ReleaseUpdatable.HaltGooglePhasedRelease, state, props.releaseId));
    const haltGoogleStagedReleaseIsLoading =
        props.stepRunComponentProps ? pauseGoogleStagedReleaseIsLoading : haltGoogleStagedReleaseIsLoadingReleaseStep;
    const [completeGoogleStagedReleaseStepRun, completeGoogleStagedReleaseIsLoadingStepRun, ,] = useCallUpdatable(
        StepRunUpdatable.CompleteGooglePhasedRelease,
        props.stepRunComponentProps,
    );

    const completeGoogleStagedReleaseIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.CompleteGooglePhasedRelease, state, props.releaseId),
    );
    useSelector((state: RootState) => getError(ReleaseUpdatable.CompleteGooglePhasedRelease, state, props.releaseId));
    useSelector((state: RootState) => getUpdated(ReleaseUpdatable.CompleteGooglePhasedRelease, state, props.releaseId));
    const completeGoogleStagedReleaseIsLoading =
        props.stepRunComponentProps ?
            completeGoogleStagedReleaseIsLoadingStepRun
        :   completeGoogleStagedReleaseIsLoadingReleaseStep;
    const [pauseApplePhasedReleaseStepRun, pauseApplePhasedReleaseIsLoadingStepRun, ,] = useCallUpdatable(
        StepRunUpdatable.PauseApplePhasedRelease,
        props.stepRunComponentProps,
    );

    const pauseApplePhasedReleaseIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.PauseApplePhasedRelease, state, props.releaseId),
    );
    useSelector((state: RootState) => getError(ReleaseUpdatable.PauseApplePhasedRelease, state, props.releaseId));
    useSelector((state: RootState) => getUpdated(ReleaseUpdatable.PauseApplePhasedRelease, state, props.releaseId));
    const pauseApplePhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            pauseApplePhasedReleaseIsLoadingStepRun
        :   pauseApplePhasedReleaseIsLoadingReleaseStep;
    const [resumeApplePhasedReleaseStepRun, resumeApplePhasedReleaseIsLoadingStepRun, ,] = useCallUpdatable(
        StepRunUpdatable.ResumeApplePhasedRelease,
        props.stepRunComponentProps,
    );

    const resumeApplePhasedReleaseIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.ResumeApplePhasedRelease, state, props.releaseId),
    );
    useSelector((state: RootState) => getError(ReleaseUpdatable.ResumeApplePhasedRelease, state, props.releaseId));
    useSelector((state: RootState) => getUpdated(ReleaseUpdatable.ResumeApplePhasedRelease, state, props.releaseId));
    const resumeApplePhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            resumeApplePhasedReleaseIsLoadingStepRun
        :   resumeApplePhasedReleaseIsLoadingReleaseStep;
    const [completeApplePhasedReleaseStepRun, completeApplePhasedReleaseIsLoadingStepRun] = useCallUpdatable(
        StepRunUpdatable.CompleteApplePhasedRelease,
        props.stepRunComponentProps,
    );

    const completeApplePhasedReleaseIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.CompleteApplePhasedRelease, state, props.releaseId),
    );
    useSelector((state: RootState) => getError(ReleaseUpdatable.CompleteApplePhasedRelease, state, props.releaseId));
    useSelector((state: RootState) => getUpdated(ReleaseUpdatable.CompleteApplePhasedRelease, state, props.releaseId));
    const completeApplePhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            completeApplePhasedReleaseIsLoadingStepRun
        :   completeApplePhasedReleaseIsLoadingReleaseStep;
    const [completeHuaweiPhasedReleaseStepRun, completeHuaweiPhasedReleaseIsLoadingStepRun, ,] = useCallUpdatable(
        StepRunUpdatable.CompleteHuaweiPhasedRelease,
        props.stepRunComponentProps,
    );

    const completeHuaweiPhasedReleaseIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.CompleteHuaweiPhasedRelease, state, props.releaseId),
    );
    useSelector((state: RootState) => getError(ReleaseUpdatable.CompleteHuaweiPhasedRelease, state, props.releaseId));
    useSelector((state: RootState) => getUpdated(ReleaseUpdatable.CompleteHuaweiPhasedRelease, state, props.releaseId));
    const completeHuaweiPhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            completeHuaweiPhasedReleaseIsLoadingStepRun
        :   completeHuaweiPhasedReleaseIsLoadingReleaseStep;
    const [pauseHuaweiPhasedReleaseStepRun, pauseHuaweiPhasedReleaseIsLoadingStepRun, ,] = useCallUpdatable(
        StepRunUpdatable.PauseHuaweiPhasedRelease,
        props.stepRunComponentProps,
    );

    const pauseHuaweiPhasedReleaseIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.PauseHuaweiPhasedRelease, state, props.releaseId),
    );
    useSelector((state: RootState) => getError(ReleaseUpdatable.PauseHuaweiPhasedRelease, state, props.releaseId));
    useSelector((state: RootState) => getUpdated(ReleaseUpdatable.PauseHuaweiPhasedRelease, state, props.releaseId));
    const pauseHuaweiPhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            pauseHuaweiPhasedReleaseIsLoadingStepRun
        :   pauseHuaweiPhasedReleaseIsLoadingReleaseStep;
    const [startStagedRolloutStepRun, startStagedRolloutIsLoadingStepRun] = useCallUpdatable(
        StepRunUpdatable.StartAutomatedAndroidStagedRollout,
        props.stepRunComponentProps,
    );

    const startStagedRolloutIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.StartAutomatedStagedRollout, state, props.releaseId),
    );
    useSelector((state: RootState) => getError(ReleaseUpdatable.StartAutomatedStagedRollout, state, props.releaseId));
    useSelector((state: RootState) => getUpdated(ReleaseUpdatable.StartAutomatedStagedRollout, state, props.releaseId));
    const startStagedRolloutIsLoading =
        props.stepRunComponentProps ? startStagedRolloutIsLoadingStepRun : startStagedRolloutIsLoadingReleaseStep;
    const startAutomatedStagedRollout = () => {
        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
        };

        if (props.stepRunComponentProps) {
            startStagedRolloutStepRun();
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.StartAutomatedStagedRollout, request, props.releaseId));
        }
    };

    const haltGoogleStagedRollout = () => {
        const request: UpdateGooglePhasedReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            userFraction: 0,
            phaseState: GooglePlayStoreState.Halted,
            actionType: RolloutAction.Halt,
        };

        if (props.stepRunComponentProps) {
            pauseGoogleStagedRelease(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.HaltGooglePhasedRelease, request, props.releaseId));
        }
    };

    const haltHuaweiPhasedRelease = () => {
        const request: UpdateHuaweiPhasedReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            userFraction: 0,
            phaseState: HuaweiPhasedReleaseState.Suspended,
            actionType: RolloutAction.Halt,
        };

        if (props.stepRunComponentProps) {
            pauseHuaweiPhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.PauseHuaweiPhasedRelease, request, props.releaseId));
        }
    };

    const completeHuaweiPhasedRelease = () => {
        const request: UpdateHuaweiPhasedReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            userFraction: 1,
            phaseState: HuaweiPhasedReleaseState.Released,
            actionType: RolloutAction.UpdatePercentage,
        };

        if (props.stepRunComponentProps) {
            completeHuaweiPhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.CompleteHuaweiPhasedRelease, request, props.releaseId));
        }
    };

    const completeGoogleStagedRollout = () => {
        const request: UpdateGooglePhasedReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            userFraction: 1,
            phaseState: GooglePlayStoreState.Completed,
            actionType: RolloutAction.UpdatePercentage,
        };

        if (props.stepRunComponentProps) {
            completeGoogleStagedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.CompleteGooglePhasedRelease, request, props.releaseId));
        }
    };

    const pauseApplePhasedRelease = () => {
        const request: UpdateApplePhasedReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            phaseState: AppleAppStorePhasedReleaseState.Paused,
            actionType: RolloutAction.Halt,
            userFraction: null,
        };

        if (props.stepRunComponentProps) {
            pauseApplePhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.PauseApplePhasedRelease, request, props.releaseId));
        }
    };

    const resumeApplePhasedRelease = () => {
        const request: UpdateApplePhasedReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            phaseState: AppleAppStorePhasedReleaseState.Active,
            actionType: RolloutAction.Resume,
            // TODO: populate based on form (RUN-4644)
            resumeAutomations: [
                AutomationType.AutoPromoteStablePhasedReleases,
                AutomationType.AutoHaltUnstablePhasedReleases,
            ],
            userFraction: null,
        };

        if (props.stepRunComponentProps) {
            resumeApplePhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.ResumeApplePhasedRelease, request, props.releaseId));
        }
    };

    const completeApplePhasedRelease = () => {
        const request: UpdateApplePhasedReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            phaseState: AppleAppStorePhasedReleaseState.Complete,
            actionType: RolloutAction.UpdatePercentage,
            userFraction: null,
        };

        if (props.stepRunComponentProps) {
            completeApplePhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.CompleteApplePhasedRelease, request, props.releaseId));
        }
    };

    const googlePlatformStagedRolloutCtas = (needsSequencedStagedRolloutStart: boolean) => {
        if (needsSequencedStagedRolloutStart) {
            return (
                <PermissionedActionButton
                    icon={<PlayCircleFilled />}
                    onClick={startAutomatedStagedRollout}
                    releaseId={props.releaseId}
                    loading={startStagedRolloutIsLoading}
                    type="primary"
                >
                    Start automated rollout sequence
                </PermissionedActionButton>
            );
        }

        const haltRolloutButton = (
            <PermissionedActionButton
                actionType={RunwayUserAction.UAHaltRollout}
                icon={<PauseOutlined style={{ color: runwayColors.statusRed }} />}
                onClick={() => haltGoogleStagedRollout()}
                loading={haltGoogleStagedReleaseIsLoading}
                disabled={phasedReleasePaused || isSuperseded}
                customDisallowedMessage="You do not have permission to halt rollouts"
                releaseId={props.releaseId}
            >
                Halt rollout
            </PermissionedActionButton>
        );

        return (
            <Space>
                {isSuperseded ?
                    <Tooltip title="This version was superseded and cannot be halted." placement="top">
                        {haltRolloutButton}
                    </Tooltip>
                :   haltRolloutButton}
                <PermissionedActionButton
                    actionType={
                        phasedReleasePaused ?
                            RunwayUserAction.UAResumeRollout
                        :   RunwayUserAction.UAUpdateRolloutPercentage
                    }
                    icon={<PercentageOutlined style={{ color: runwayColors.info }} />}
                    onClick={() => props.setStagedRolloutEditModalVisible(true)}
                    customDisallowedMessage={
                        phasedReleasePaused ?
                            'You do not have permission to resume rollouts'
                        :   'You do not have permission to change rollout percentage'
                    }
                    releaseId={props.releaseId}
                >
                    {phasedReleasePaused ? 'Resume rollout' : 'Edit staged rollout'}
                </PermissionedActionButton>
                <Popconfirm
                    title="Are you sure you want to release to 100% now?"
                    okText="Yes, release to 100% now."
                    placement="topRight"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => completeGoogleStagedRollout()}
                >
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                        icon={<RiseOutlined style={{ color: runwayColors.statusGreen }} />}
                        loading={completeGoogleStagedReleaseIsLoading}
                        releaseId={props.releaseId}
                        customDisallowedMessage="You do not have permission to release to 100% of users"
                    >
                        Release to 100% now
                    </PermissionedActionButton>
                </Popconfirm>
            </Space>
        );
    };

    const huaweiPhasedReleaseCtas = (needsSequencedStagedRolloutStart: boolean) => {
        if (needsSequencedStagedRolloutStart) {
            return (
                <PermissionedActionButton
                    icon={<PlayCircleFilled />}
                    onClick={startAutomatedStagedRollout}
                    releaseId={props.releaseId}
                    loading={startStagedRolloutIsLoading}
                    type="primary"
                >
                    Start automated rollout sequence
                </PermissionedActionButton>
            );
        }

        return (
            <Space>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAHaltRollout}
                    icon={<PauseOutlined style={{ color: runwayColors.statusRed }} />}
                    onClick={() => haltHuaweiPhasedRelease()}
                    loading={pauseHuaweiPhasedReleaseIsLoading}
                    disabled={phasedReleasePaused || rolloutComplete}
                    customDisallowedMessage="You do not have permission to halt phased releases"
                    releaseId={props.releaseId}
                >
                    Halt phased release
                </PermissionedActionButton>
                <PermissionedActionButton
                    actionType={
                        phasedReleasePaused ?
                            RunwayUserAction.UAResumeRollout
                        :   RunwayUserAction.UAUpdateRolloutPercentage
                    }
                    icon={<PercentageOutlined style={{ color: runwayColors.info }} />}
                    onClick={() => props.setStagedRolloutEditModalVisible(true)}
                    releaseId={props.releaseId}
                    customDisallowedMessage={
                        phasedReleasePaused ?
                            'You do not have permission to resume phased releases'
                        :   'You do not have permission to change rollout percentage'
                    }
                    disabled={rolloutComplete}
                >
                    {phasedReleasePaused ? 'Resume phased release' : 'Edit phased release'}
                </PermissionedActionButton>
                <Popconfirm
                    title="Are you sure you want to release to 100% now?"
                    okText="Yes, release to 100% now."
                    placement="topRight"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => completeHuaweiPhasedRelease()}
                >
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                        icon={<RiseOutlined style={{ color: runwayColors.statusGreen }} />}
                        loading={completeHuaweiPhasedReleaseIsLoading}
                        releaseId={props.releaseId}
                        disabled={rolloutComplete}
                        customDisallowedMessage="You do not have permission to release to 100% of users"
                    >
                        Release to 100% now
                    </PermissionedActionButton>
                </Popconfirm>
            </Space>
        );
    };

    const resumePhasedReleaseIcon = <CaretRightOutlined style={{ color: runwayColors.statusGreen }} />;
    const resumeAllOption = {
        id: 'resumeAll',
        title: 'Resume rollout and all rollout automations',
    };

    const resumeSelectOption = {
        id: 'resumeSelect',
        title: 'Resume rollout and select automations...',
    };

    const resumeOptions = [resumeAllOption, resumeSelectOption];

    const resumeRolloutMenu = (
        <Menu
            onClick={(item) => {
                switch (item.key) {
                    case resumeAllOption.id:
                        props.setAutoSelectAutomations(true);
                        props.setStagedRolloutEditModalVisible(true);
                        break;
                    case resumeSelectOption.id:
                        props.setAutoSelectAutomations(false);
                        props.setStagedRolloutEditModalVisible(true);
                        break;
                    default:
                        console.log(`Unknown menu item ${item.key}`);
                }
            }}
            items={resumeOptions.map((option) => ({
                label: option.title,
                key: option.id,
                icon: resumePhasedReleaseIcon,
            }))}
        />
    );

    const needsAutomatedRolloutStart =
        (isGooglePlayStore || isHuaweiAppGallery) && automatedStagedRolloutEnabled && !rolloutStartedAt;

    return (
        <>
            {isGooglePlayStore && googlePlatformStagedRolloutCtas(needsAutomatedRolloutStart)}
            {isHuaweiAppGallery && huaweiPhasedReleaseCtas(needsAutomatedRolloutStart)}
            {isAppleAppStore && (
                <Space>
                    {!phasedReleasePaused && (
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAHaltRollout}
                            icon={<PauseOutlined style={{ color: runwayColors.statusRed }} />}
                            onClick={() => pauseApplePhasedRelease()}
                            loading={pauseApplePhasedReleaseIsLoading}
                            releaseId={props.releaseId}
                            disabled={rolloutComplete}
                            customDisallowedMessage="You do not have permission to halt phased releases"
                        >
                            Pause phased release
                        </PermissionedActionButton>
                    )}
                    {phasedReleasePaused && (
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAResumeRollout}
                            icon={resumePhasedReleaseIcon}
                            onClick={() => resumeApplePhasedRelease()}
                            overlay={resumeRolloutMenu}
                            loading={resumeApplePhasedReleaseIsLoading}
                            releaseId={props.releaseId}
                            disabled={rolloutComplete}
                            customDisallowedMessage="You do not have permission to resume rollouts"
                        >
                            Resume rollout
                        </PermissionedActionButton>
                    )}
                    <Popconfirm
                        title="Are you sure you want to release to all users now?"
                        okText="Yes, release to all users now."
                        placement="topRight"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => completeApplePhasedRelease()}
                    >
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                            icon={<RiseOutlined style={{ color: runwayColors.statusGreen }} />}
                            loading={completeApplePhasedReleaseIsLoading}
                            releaseId={props.releaseId}
                            disabled={rolloutComplete}
                            customDisallowedMessage="You do not have permission to release to 100% of users"
                        >
                            Release to all users now
                        </PermissionedActionButton>
                    </Popconfirm>
                </Space>
            )}
        </>
    );
};

export interface PhasedReleaseModuleBaseProps extends PhaseReleaseModuleProps {
    stagedRolloutEditModalVisible: boolean;
    setStagedRolloutEditModalVisible: (visible: boolean) => void;
    autoSelectAutomations: boolean;
    setAutoSelectAutomations: (autoSelect: boolean) => void;
    shouldContrastPercentagesModule?: boolean;
    autoHaltUnstablePhasedReleasesAutomationId: Nullable<string>;
    autoPromoteStablePhasedReleasesAutomationId: Nullable<string>;
    autoStagedRolloutAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

export const PhasedReleaseModuleBase: React.FC<PhasedReleaseModuleBaseProps> = (props) => {
    usePhasedReleaseMessages({
        appId: props.appId,
        releaseId: props.releaseId,
        stepRunComponentProps: props.stepRunComponentProps,
    });

    const rolloutStartedAt = props.phasedRelease?.startDate;

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;

    const daysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const isGooglePlayStore = getIsGooglePlayStore(props.appStoreType);
    const isAppleAppStore = getIsAppleAppStore(props.appStoreType);

    const rolloutComplete = isPhasedReleaseComplete(props.phasedRelease);
    const phasedReleasePaused = isPhasedReleasePaused(props.phasedRelease?.state);
    const percentColor = phasedReleaseColorForState(
        props.platform,
        props.phasedRelease?.state as AppleAppStorePhasedReleaseState,
        props.googleState,
    );
    const userFraction =
        isGooglePlayStore ? props.phasedRelease?.userFraction
        : isAppleAppStore ?
            props.phasedRelease?.isManuallyAcceleratedPhasedRollout || props.phasedRelease?.isRunwayAcceleratedRollout ?
                1
            :   props.phasedRelease?.userFraction
        :   null;

    const canUserEditUserFraction = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateRolloutPercentage, props.releaseId, null, ''),
    );

    const hasPhasedReleaseState = !!props.phasedRelease;

    const isPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    const androidStagedRolloutNotStarted = isGooglePlayStore && automatedStagedRolloutEnabled && !rolloutStartedAt;

    const percentText = getRolloutPercentText(isGooglePlayStore, rolloutComplete, userFraction);

    const phasedReleasePercentModule = (
        <>
            <PhasedReleasePercent
                appStoreType={props.appStoreType}
                isRolloutComplete={rolloutComplete}
                userFraction={userFraction}
                percentColor={percentColor}
            />
            {props.phasedRelease && isPhasedRollout && (
                <PhasedReleaseProgress
                    appId={props.appId}
                    {...props.phasedRelease}
                    progressStyle={{ marginRight: 12, marginLeft: 12, width: 180 }}
                    autoStagedRolloutAutomationId={props.autoStagedRolloutAutomationId}
                />
            )}
        </>
    );

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {props.shouldContrastPercentagesModule ?
                    <div
                        style={{
                            borderRadius: 8,
                            backgroundColor: antGrey.grey1,
                            padding: '20px 24px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            border: `1px solid ${antGrey.grey4}`,
                        }}
                    >
                        {phasedReleasePercentModule}
                    </div>
                :   <Space size={24} style={{ alignItems: 'flex-end' }}>
                        phasedReleasePercentModule
                    </Space>
                }
                {isPhasedRollout && !rolloutComplete && (
                    <PhasedReleaseDayText
                        appId={props.appId}
                        daysToPercent={daysToPercent}
                        appStoreType={props.appStoreType}
                        phasedReleaseDay={props.phasedRelease?.dayNumber ?? 0}
                        phasedRelease={props.phasedRelease}
                        isPhasedReleasePaused={phasedReleasePaused}
                        androidStagedRolloutNotStarted={androidStagedRolloutNotStarted}
                        maxWidth={500}
                        currentPercent={percentText}
                    />
                )}
            </div>
            {props.phasedRelease && !rolloutComplete && (
                <UpdateStagedRolloutModal
                    stepRunComponentProps={props.stepRunComponentProps}
                    autoSelectAutomations={props.autoSelectAutomations}
                    platform={props.platform}
                    appStoreType={props.appStoreType}
                    orgId={props.orgId}
                    appId={props.appId}
                    releaseId={props.releaseId}
                    phasedRelease={props.phasedRelease}
                    open={props.stagedRolloutEditModalVisible}
                    canEditUserFraction={canUserEditUserFraction}
                    isPaused={phasedReleasePaused}
                    userFraction={userFraction}
                    onSuccess={() => props.setStagedRolloutEditModalVisible(false)}
                    onClose={() => props.setStagedRolloutEditModalVisible(false)}
                    autoPromoteStablePhasedReleasesAutomationId={props.autoPromoteStablePhasedReleasesAutomationId}
                    autoHaltUnstablePhasedReleasesAutomationId={props.autoHaltUnstablePhasedReleasesAutomationId}
                />
            )}
        </>
    );
};

export const phasedReleaseColorForState: (
    platform: AppPlatform,
    appleState?: AppleAppStorePhasedReleaseState,
    googleState?: GooglePlayStoreState,
) => string = (platform, appleState, googleState?) => {
    switch (platform) {
        case AppPlatform.TvOS:
        case AppPlatform.Ios:
        case AppPlatform.VisionOS: {
            switch (appleState) {
                case AppleAppStorePhasedReleaseState.Paused:
                case AppleAppStorePhasedReleaseState.Active:
                    return runwayColors.statusYellow;
                case AppleAppStorePhasedReleaseState.Complete:
                    return runwayColors.statusGreen;
                case AppleAppStorePhasedReleaseState.Inactive:
                    return runwayColors.statusGrey;
                default:
                    return runwayColors.statusGrey;
            }
        }
        case AppPlatform.WearOS:
        case AppPlatform.Android: {
            switch (googleState) {
                case GooglePlayStoreState.Halted:
                case GooglePlayStoreState.InProgress:
                    return runwayColors.statusYellow;
                case GooglePlayStoreState.Completed:
                    return runwayColors.statusGreen;
                case GooglePlayStoreState.Draft:
                case GooglePlayStoreState.StatusUnspecified:
                    return runwayColors.statusGrey;
                default:
                    return runwayColors.statusGrey;
            }
        }
        default:
            return runwayColors.statusGrey;
    }
};
